import type { ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import NotFoundPage from '../../shared/NotFoundPage';

import CatalogPage from './catalog/CatalogPage';
import PayPeriodsPage from './PayPeriodsPage';
import RegionSubregionManager from './RegionSubregionManager';
import SettingsPageShell from './SettingsPageShell';
import ShiftPayRatesPage from './ShiftPayRatesPage';
import TreeSpeciesPage from './TreeSpeciesPage';
import AuditPage from './users/AuditPage';
import UsersPage from './users/UsersPage';
import WarehouseDetailPage from './WarehouseDetailPage';
import WarehouseManagement from './WarehouseManagement';
import WorkshopTypeEditPage from './workshopTypes/WorkshopTypeEditPage';
import WorkshopTypesPage from './workshopTypes/WorkshopTypesPage';

/**
 * The routed sub-pages, as a table rather than 80 lines of near-identical JSX.
 *
 * Every shell-wrapped page differs only in path, title and element, so listing
 * them as data keeps this flat as the settings area grows — the inline form
 * breached the lint size cap twice in two issues.
 */
const SHELL_ROUTES: Array<{ path: string; title: string; element: ReactElement }> = [
  { path: 'tree-species', title: 'Tree Species', element: <TreeSpeciesPage /> },
  { path: 'workshop-types', title: 'Workshop Types', element: <WorkshopTypesPage /> },
  { path: 'regions', title: 'Regions', element: <RegionSubregionManager /> },
  { path: 'warehouses', title: 'Warehouses', element: <WarehouseManagement /> },
  { path: 'catalog', title: 'Item Catalog', element: <CatalogPage /> },
  { path: 'pay-rates', title: 'Shift Pay Rates', element: <ShiftPayRatesPage /> },
  { path: 'pay-periods', title: 'Pay Calendar', element: <PayPeriodsPage /> },
];

export default function SettingsRoutes() {
  return (
    <Routes>
      <Route index element={<Navigate to="users" replace />} />
      {SHELL_ROUTES.map(({ path, title, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <SettingsPageShell crumb="Settings · Bonsai Bar" title={title}>
              {element}
            </SettingsPageShell>
          }
        />
      ))}
      {/* Its own crumb (Settings · Workshop Types), so it stays out of the table. */}
      <Route
        path="workshop-types/:id"
        element={
          <SettingsPageShell crumb="Settings · Workshop Types" title="Workshop Type">
            <WorkshopTypeEditPage />
          </SettingsPageShell>
        }
      />
      {/* These own their full-page chrome, so no shell wrapper. */}
      <Route path="warehouses/:id" element={<WarehouseDetailPage />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="audit" element={<AuditPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
